import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Video, Loader2, X, Clapperboard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import ImageUploader from "@/components/ImageUploader";
import { MusicPicker, LocationPicker } from "@/components/MusicLocation";
import ReelTrimEditor from "@/components/ReelTrimEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateReelDialog({ open, onOpenChange, onCreated }) {
  const [videoUrl, setVideoUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [music, setMusic] = useState(null);
  const [location, setLocation] = useState(null);
  const [visibility, setVisibility] = useState("public");
  const [publishing, setPublishing] = useState(false);
  const [edit, setEdit] = useState(null); // {trim_start, trim_end, cover_time, cover_url}
  const [step, setStep] = useState(null); // "Processando..." label

  const reset = () => { setVideoUrl(""); setCaption(""); setMusic(null); setLocation(null); setVisibility("public"); setEdit(null); setStep(null); };

  const publish = async () => {
    if (!videoUrl) { toast.error("Envie um vídeo para o Reel"); return; }
    setPublishing(true);
    try {
      let finalVideo = videoUrl, coverUrl = edit?.cover_url || null, duration = null;
      const needsTrim = edit && (edit.trim_start > 0.05 || (edit.trim_end != null && edit.trim_end < (edit._duration ?? Infinity) - 0.05));
      if (needsTrim) {
        setStep("Cortando o vídeo no servidor...");
        const r = await axios.post(`${API}/reels/process`, { video_url: videoUrl, trim_start: edit.trim_start || 0, trim_end: edit.trim_end ?? null, cover_time: edit.cover_time ?? edit.trim_start ?? 0 });
        finalVideo = r.data.video_url; duration = r.data.duration;
        if (!coverUrl) coverUrl = r.data.cover_url;
      } else if (!coverUrl) {
        setStep("Gerando a capa...");
        try {
          const r = await axios.post(`${API}/reels/cover`, { video_url: videoUrl, cover_time: edit?.cover_time ?? 0 });
          coverUrl = r.data.cover_url;
        } catch { /* cover is optional */ }
      }
      setStep("Publicando...");
      const r = await axios.post(`${API}/posts`, { content: caption.trim(), video_url: finalVideo, cover_url: coverUrl, duration, music, location, visibility, is_reel: true });
      toast.success(r.data.moderation_status === "pending" ? "Reel enviado para moderação!" : "Reel publicado! 🎬");
      onCreated?.(r.data);
      reset();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao publicar"); }
    setPublishing(false); setStep(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!publishing) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl" data-testid="create-reel-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Clapperboard className="w-5 h-5 text-primary" /> Criar Reel</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-[260px_1fr] gap-4">
          <div>
            {videoUrl ? (
              <div className="relative">
                <ReelTrimEditor videoUrl={videoUrl} value={edit} onChange={setEdit} />
                <button onClick={() => { setVideoUrl(""); setEdit(null); }} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center z-10" aria-label="Remover vídeo" data-testid="reel-remove-video"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="aspect-[9/16] max-h-[360px] rounded-2xl bg-black border border-border flex items-center justify-center" data-testid="reel-preview">
                <div className="text-center p-4 text-white/70">
                  <Video className="w-10 h-10 mx-auto mb-2" />
                  <p className="text-xs">Prévia 9:16</p>
                  <p className="text-[10px] mt-1 text-white/50">Depois de enviar você poderá cortar e escolher a capa</p>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <ImageUploader testid="reel-upload-btn" label={videoUrl ? "Trocar vídeo" : "Enviar vídeo"} accept="video/*" variant={videoUrl ? "outline" : "default"} size="default" className="w-full" onUploaded={(url) => setVideoUrl(url)} />
            <Textarea rows={4} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Escreva uma legenda... #hashtags e @menções funcionam" data-testid="reel-caption" />
            <div className="flex flex-col gap-2">
              <MusicPicker value={music} onChange={setMusic} />
              <LocationPicker value={location} onChange={setLocation} />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Visibilidade:</span>
              <div className="inline-flex rounded-full bg-muted p-0.5">
                {[["public", "🌍 Público"], ["private", "🔒 Só seguidores"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setVisibility(k)} data-testid={`reel-visibility-${k}`} className={`px-3 py-1 rounded-full ${visibility === k ? "bg-background shadow font-semibold" : ""}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={publishing}>Cancelar</Button>
          <Button onClick={publish} disabled={publishing || !videoUrl} className="rounded-full" data-testid="reel-publish-btn">
            {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Clapperboard className="w-4 h-4 mr-1" />} {step || "Publicar Reel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
